import { Link, useLocation } from "wouter";
import { useState, ReactNode, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/integrated-auth";
import { useAuth as useFirebaseAuth } from "@/contexts/firebase-auth";
import { useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { UserAvatar } from '@/components/ui/user-avatar';
import JoinLeagueDialog from "@/components/dialogs/join-league-dialog";
import { NotificationMenu } from "@/components/layout/notification-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTheme } from "@/components/theme-provider";
import SiteLogo from "@/components/SiteLogo";
import ErrorDisplay from "@/components/debug/ErrorDisplay";
import { useSwipeBack } from "@/hooks/use-swipe-back";
import { useKeyboardVisible } from "@/hooks/use-keyboard-visible";
import { useChatContextSafe } from "@/contexts/chat-context";
import { GlobalChatSheet } from "@/components/chat/global-chat-sheet";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { hasFeatureAccess } from "@/lib/beta-access";
import { OtaVersionBadge } from "@/components/OtaVersionBadge";

// Import all necessary icons directly from Lucide React
import {
  Sparkles,
  Trophy,
  PlusCircle,
  User,
  MapPin,
  MessageSquare,
  Settings,
  LogOut,
  Moon,
  Sun,
  Shield,
  BadgeHelp,
  HelpCircle,
  CalendarClock,
  Radio,
  Zap,
  Package,
} from "lucide-react";

interface LayoutProps {
  children: ReactNode;
  pageTitle?: string;
}

export default function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const { t } = useTranslation('nav');
  const { user, isAuthenticated } = useAuth();
  const { signOut } = useFirebaseAuth();
  const queryClient = useQueryClient();
  const chatContext = useChatContextSafe();
  const { zapBetaOnly, crateBetaOnly } = useSiteSettings();
  const canAccessZap = hasFeatureAccess(zapBetaOnly, user);
  const canAccessCrate = hasFeatureAccess(crateBetaOnly, user);

  const handleSignOut = async () => {
    try {
      console.log('🚪 [LAYOUT DEBUG] ========== SIGN OUT STARTED ==========');
      console.log('🚪 [LAYOUT DEBUG] Current user:', user?.email);

      // Set sign-out flag FIRST to prevent auto-restoration
      const signOutTimestamp = Date.now().toString();
      localStorage.setItem('yapzap_signed_out', signOutTimestamp);
      console.log('🚪 [LAYOUT DEBUG] ✓ Set yapzap_signed_out flag:', signOutTimestamp);

      // Call Firebase signOut
      await signOut();
      console.log('🚪 [LAYOUT DEBUG] ✓ Firebase sign out completed');

      // Clear React Query cache
      queryClient.clear();
      console.log('🚪 [LAYOUT DEBUG] ✓ React Query cache cleared');

      // Clear all localStorage items
      localStorage.removeItem('userToken');
      localStorage.removeItem('userPreferences');
      console.log('🚪 [LAYOUT DEBUG] ✓ localStorage cleared');

      // Clear IndexedDB (Firebase persistence)
      if (window.indexedDB) {
        const databases = await window.indexedDB.databases();
        databases.forEach(db => {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
          }
        });
        console.log('🚪 [LAYOUT DEBUG] ✓ IndexedDB cleared');
      }

      console.log('🚪 [LAYOUT DEBUG] ✓ Redirecting to /login');
      // Redirect to login page
      window.location.href = '/login';

      console.log('🚪 [LAYOUT DEBUG] ========== SIGN OUT COMPLETED ==========');
    } catch (error) {
      console.error('🚪 [LAYOUT DEBUG] ❌ Error during sign out:', error);
    }
  };

  const isActive = (path: string) => {
    // For You page can be accessed via "/" or "/for-you"
    if (path === "/for-you") return location === "/" || location === "/for-you";
    if (path === "/") return location === "/";
    // Don't highlight Create League button on onboarding page
    if (path === "/create" && location === "/onboarding") return false;
    return location.startsWith(path);
  };
  const isChatActive = !!chatContext?.isChatOpen;

  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  // Enable swipe-back navigation, but disable when modals are open
  const hasModalOpen = joinDialogOpen || mobileMenuOpen;
  useSwipeBack({
    disabled: hasModalOpen || !isAuthenticated || location === '/'
  });

  // Track keyboard visibility to hide bottom nav when keyboard is open
  const isKeyboardVisible = useKeyboardVisible();

  // Background texture is now managed in the user settings page
  // But we still need to read and apply the texture from localStorage
  useEffect(() => {
    const applyBackgroundTexture = () => {
      console.log('🎨 [LAYOUT] applyBackgroundTexture called');
      const body = document.body;

      // Remove all existing texture classes
      body.classList.remove(
        'bg-texture-food',
        'bg-texture-anchors',
        'bg-texture-skulls',
        'bg-texture-piano',
        'bg-texture-motion',
        'bg-texture-hexagons',
        // Legacy names (cleanup for users who had old textures)
        'bg-texture-subtle',
        'bg-texture-dots',
        'bg-texture-grid',
        'bg-texture-waves',
        'bg-texture-circuit',
        'bg-texture-wiggle',
        'bg-texture-stripes',
        'bg-texture-signal',
        'bg-texture-clouds',
        'bg-texture-glamorous'
      );
      console.log('🎨 [LAYOUT] Removed all texture classes');

      // Check preferences in localStorage
      const preferences = localStorage.getItem('userPreferences');
      const legacyTexture = localStorage.getItem('bgTexture');
      console.log('🎨 [LAYOUT] userPreferences from localStorage:', preferences);
      console.log('🎨 [LAYOUT] legacy bgTexture from localStorage:', legacyTexture);

      let bgTexture = 'none';

      if (preferences) {
        try {
          const parsedPreferences = JSON.parse(preferences);
          if (parsedPreferences.bgTexture) {
            bgTexture = parsedPreferences.bgTexture;
          }
        } catch (error) {
          console.error('🎨 [LAYOUT] Error parsing user preferences:', error);
        }
      } else {
        // Check for legacy background texture setting
        const storedBgTexture = localStorage.getItem('bgTexture');
        if (storedBgTexture) {
          bgTexture = storedBgTexture;
        }
      }

      console.log('🎨 [LAYOUT] Final bgTexture to apply:', bgTexture);

      // Apply the texture class based on the preference
      if (bgTexture !== 'none') {
        const textureClass = `bg-texture-${bgTexture}`;
        body.classList.add(textureClass);
        console.log('🎨 [LAYOUT] Added texture class:', textureClass);
        console.log('🎨 [LAYOUT] Body classes now:', body.className);
      } else {
        console.log('🎨 [LAYOUT] No texture to apply (none)');
      }
    };

    // Apply texture on mount
    console.log('🎨 [LAYOUT] Setting up background texture listeners');
    applyBackgroundTexture();

    // Listen for storage events to sync across tabs/windows
    window.addEventListener('storage', applyBackgroundTexture);

    // Listen for custom event when settings are saved
    window.addEventListener('bgTextureChanged', applyBackgroundTexture);

    return () => {
      console.log('🎨 [LAYOUT] Cleaning up background texture listeners');
      window.removeEventListener('storage', applyBackgroundTexture);
      window.removeEventListener('bgTextureChanged', applyBackgroundTexture);
    };
  }, []);

  // getName function for displaying username
  const getName = () => {
    if (!user) return "User";

    // Hide any Firebase data during onboarding until username is selected
    if (location === '/onboarding') {
      // Only show username if it's not a temporary one
      if (user.username && !user.username.startsWith('user_')) {
        return user.username;
      }
      // Otherwise, show nothing or placeholder while onboarding
      return null;
    }

    if (user.username) return user.username;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split("@")[0];
    return "User";
  };

  const handleFeedbackClick = () => {
    try {
      console.log('[Layout] Feedback button clicked - calling rebuilt shake feedback trigger');
      const windowWithShakeFeedback = window as Window & {
        triggerShakeFeedback?: () => void;
      };

      // Use the global function set up in ShakeFeedback component
      if (windowWithShakeFeedback.triggerShakeFeedback) {
        windowWithShakeFeedback.triggerShakeFeedback();
        console.log('[Layout] Shake feedback triggered successfully');
      } else {
        console.error('[Layout] triggerShakeFeedback function not available - ShakeFeedback component may not be initialized');
      }
    } catch (error) {
      console.error('[Layout] Error triggering shake feedback:', error);
    }
  };

  return (
    <div className="h-screen overflow-hidden text-foreground capacitor-app-container flex flex-col">
      {/* Header - fixed at top, never scrolls */}
      <header
        className="flex-shrink-0 z-[100] bg-background border-b border-border"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        <div
          className="flex h-16 md:h-24 items-center justify-between px-4"
          style={{
            paddingLeft: 'max(1rem, env(safe-area-inset-left, 1rem))',
            paddingRight: 'max(1rem, env(safe-area-inset-right, 1rem))'
          }}
        >
          <div className="flex items-center gap-4">
            <SiteLogo />
            

          </div>

          {/* Desktop Right Side */}
          <div className="hidden md:flex items-center gap-4">
            <NotificationMenu />
            <button
              onClick={handleFeedbackClick}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
              title="Send Feedback"
              aria-label="Send Feedback"
            >
              <BadgeHelp size={20} />
            </button>
            <ThemeToggle />
            
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                {location !== '/onboarding' && (
                  <Link href="/profile">
                    <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity">
                      <UserAvatar size="sm" />
                      <span className="text-sm font-medium text-foreground whitespace-nowrap">{getName()}</span>
                    </div>
                  </Link>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button asChild>
                  <Link href="/login">{t('signIn')}</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Icons */}
          <div className="md:hidden flex items-center gap-3">
            <div className="md:hidden">
              <NotificationMenu mobile={true} />
            </div>
            <button
              onClick={handleFeedbackClick}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
              title={t('sendFeedback')}
              aria-label={t('sendFeedback')}
            >
              <BadgeHelp size={20} />
            </button>
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? (
                <Sun size={20} className="text-amber-400" />
              ) : (
                <Moon size={20} className="text-indigo-500" />
              )}
            </button>
            <button
              className="text-dark"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed right-0 z-50 bg-background border border-border rounded-bl-md px-4 py-3 space-y-4 max-h-[70vh] w-64 overflow-y-auto shadow-lg animate-in slide-in-from-right"
            style={{
              top: 'calc(4rem + env(safe-area-inset-top, 0px) + 1px)',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {isAuthenticated && location !== '/onboarding' ? (
              <div className="flex items-center space-x-3 pb-3 border-b border-border">
                <UserAvatar size="md" />
                <div className="flex-1">
                  <Link href="/profile">
                    <div className="flex items-center space-x-1 group">
                      <p className="font-medium group-hover:text-primary transition-colors">{getName()}</p>
                      <Settings className="h-3 w-3 opacity-70 group-hover:text-primary transition-colors" />
                    </div>
                  </Link>
                  {user?.email && (
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  )}
                </div>
              </div>
            ) : !isAuthenticated ? (
              <div className="pb-3 border-b border-border">
                <Button asChild className="w-full">
                  <Link href="/login">{t('signIn')}</Link>
                </Button>
              </div>
            ) : null}

            <nav>
              <ul className="space-y-2">
                <li>
                  <Link href="/create">
                    <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/create") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                      <PlusCircle size={20} />
                      <span>{t('createLeague')}</span>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/for-you">
                    <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/for-you") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                      <Sparkles size={20} />
                      <span>{t('forYou')}</span>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/daily">
                    <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/daily") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                      <CalendarClock size={20} />
                      <span>{t('daily')}<sup className="ml-0.5 text-[8px] font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1 py-px rounded-full">beta</sup></span>
                    </div>
                  </Link>
                </li>

                {canAccessZap && (
                  <li>
                    <Link href="/zap">
                      <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/zap") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                        <Zap size={20} />
                        <span>{t('zap')}</span>
                      </div>
                    </Link>
                  </li>
                )}
                {canAccessCrate && (
                  <li>
                    <Link href="/crate">
                      <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/crate") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                        <Package size={20} />
                        <span>{t('myCrate')}</span>
                      </div>
                    </Link>
                  </li>
                )}
                <li>
                  <Link href="/radio">
                    <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/radio") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                      <Radio size={20} />
                      <span>{t('radio')}</span>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/settings">
                    <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/settings") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                      <div className="relative">
                        <Settings size={20} />
                      </div>
                      <span>{t('settings')}</span>
                    </div>
                  </Link>
                </li>
                {user && user.role === 'admin' && (
                  <li>
                    <Link href="/admin">
                      <div className={`flex items-center space-x-3 px-4 py-2 rounded-md ${isActive("/admin") ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-md" : "dark:dark-improved-text"}`}>
                        <Shield size={20} />
                        <span>{t('admin')}</span>
                      </div>
                    </Link>
                  </li>
                )}
                <li>
                  <div
                    onClick={handleFeedbackClick}
                    className="flex items-center space-x-3 px-4 py-2 rounded-md cursor-pointer hover:bg-accent dark:dark-improved-text"
                  >
                    <BadgeHelp size={20} className="text-blue-500" />
                    <span>{t('sendFeedback')}</span>
                  </div>
                </li>
                <li>
                  <div
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    className="flex items-center space-x-3 px-4 py-2 rounded-md cursor-pointer hover:bg-accent dark:dark-improved-text"
                  >
                    {theme === 'dark' ? (
                      <>
                        <Sun size={20} className="text-amber-400" />
                        <span>{t('lightMode')}</span>
                      </>
                    ) : (
                      <>
                        <Moon size={20} className="text-indigo-500" />
                        <span>{t('darkMode')}</span>
                      </>
                    )}
                  </div>
                </li>
                <li>
                  <Link href="/support">
                    <div className="flex items-center space-x-3 px-4 py-2 rounded-md cursor-pointer hover:bg-accent dark:dark-improved-text">
                      <HelpCircle size={20} className="text-purple-500" />
                      <span>{t('support')}</span>
                    </div>
                  </Link>
                </li>

                {isAuthenticated && (
                  <li>
                    <button
                      onClick={handleSignOut}
                      className="flex items-center space-x-3 px-4 py-2 rounded-md text-red-500 dark:text-red-400 dark:dark-improved-text font-medium"
                    >
                      <LogOut size={20} />
                      <span>{t('signOut')}</span>
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Navigation - fixed at bottom, hidden when keyboard is visible */}
      <div
        className={`md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border shadow-lg capacitor-bottom-nav ${isActive('/create') || location === '/onboarding' || isKeyboardVisible ? 'hidden' : ''}`}
        style={{
          paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))'
        }}
      >
        <div className="flex justify-around items-end pt-2 pb-1">
          <Link href="/for-you">
            <div className="flex flex-col items-center p-2 text-muted-foreground cursor-pointer">
              <Sparkles className={isActive("/for-you") ? "text-primary" : ""} size={22} />
              <span className={`text-xs mt-1 font-medium ${isActive("/for-you") ? "text-primary dark:dark-high-contrast" : "dark:dark-muted-contrast"}`}>{t('forYou')}</span>
            </div>
          </Link>
          {isAuthenticated && chatContext ? (
            <button
              onClick={() => chatContext.openChat()}
              className="relative flex flex-col items-center p-2 text-muted-foreground cursor-pointer"
              aria-label={`Open chat${chatContext.totalUnreadCount > 0 ? ` (${chatContext.totalUnreadCount} unread)` : ''}`}
            >
              <div className="relative">
                <MessageSquare className={chatContext.totalUnreadCount > 0 ? "text-primary" : ""} size={22} />
                {chatContext.totalUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 text-[9px] font-bold flex items-center justify-center rounded-full bg-red-500 text-white">
                    {chatContext.totalUnreadCount > 99 ? '99+' : chatContext.totalUnreadCount}
                  </span>
                )}
              </div>
              <span className={`text-xs mt-1 font-medium ${chatContext.totalUnreadCount > 0 ? "text-primary dark:dark-high-contrast" : "dark:dark-muted-contrast"}`}>{t('chat')}</span>
            </button>
          ) : (
            <Link href="/leagues">
              <div className="flex flex-col items-center p-2 text-muted-foreground cursor-pointer">
                <Trophy className={isActive("/leagues") ? "text-primary" : ""} size={22} />
                <span className={`text-xs mt-1 font-medium ${isActive("/leagues") ? "text-primary dark:dark-high-contrast" : "dark:dark-muted-contrast"}`}>{t('leagues')}</span>
              </div>
            </Link>
          )}

          {/* Floating Zap Button - center position */}
          {canAccessZap && (
            <Link href="/zap" onClick={() => { if (isActive("/zap")) window.dispatchEvent(new CustomEvent("zap:reset")); }}>
              <div
                className="relative -top-4 flex flex-col items-center justify-center w-14 h-14 rounded-full text-white shadow-lg shadow-purple-900/30 active:scale-95 transition-all duration-200"
                style={{ background: `linear-gradient(135deg, #7c3aed, #db2777)` }}
                aria-label="Zap a song"
              >
                <Zap size={22} />
                <span className="text-[9px] mt-0.5 font-semibold">{t('zap')}</span>
              </div>
            </Link>
          )}

          <Link href="/scene">
            <div className="flex flex-col items-center p-2 text-muted-foreground cursor-pointer">
              <MapPin className={isActive("/scene") ? "text-primary" : ""} size={22} />
              <span className={`text-xs mt-1 font-medium ${isActive("/scene") ? "text-primary dark:dark-high-contrast" : "dark:dark-muted-contrast"}`}>{t('scene')}</span>
            </div>
          </Link>
          <Link href="/daily">
            <div className="flex flex-col items-center p-2 text-muted-foreground cursor-pointer">
              <CalendarClock className={isActive("/daily") ? "text-primary" : ""} size={22} />
              <span className={`text-xs mt-1 font-medium ${isActive("/daily") ? "text-primary dark:dark-high-contrast" : "dark:dark-muted-contrast"}`}>{t('daily')}<sup className="ml-0.5 text-[6px] font-semibold text-purple-500">beta</sup></span>
            </div>
          </Link>
          {!isAuthenticated && (
            <Link href="/login">
              <div className="flex flex-col items-center p-2 text-muted-foreground cursor-pointer">
                <User className="text-orange-500" size={22} />
                <span className="text-xs mt-1 font-medium text-orange-500">{t('signIn')}</span>
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Global Chat Sheet */}
      {chatContext && <GlobalChatSheet />}

      {/* Main Content - this is the scroll container */}
      <main className="flex-1 overflow-y-auto capacitor-scroll-content">
        <div className="min-h-full flex">
          {/* Desktop Sidebar - hide during onboarding */}
          {location !== '/onboarding' && (
            <div className="hidden md:block w-64 pr-6">
              <nav className="sticky top-0 pt-4">
              <ul className="space-y-2">
                {isAuthenticated && (
                  <li>
                    <Link href="/create">
                      <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/create") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                        <PlusCircle size={20} />
                        <span>{t('createLeague')}</span>
                      </div>
                    </Link>
                  </li>
                )}
                <li>
                  <Link href="/for-you">
                    <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/for-you") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                      <Sparkles size={20} />
                      <span>{t('forYou')}</span>
                    </div>
                  </Link>
                </li>
                {isAuthenticated && chatContext && (
                  <li>
                    <button
                      onClick={() => chatContext.openChat()}
                      className={`nav-menu-item w-full flex items-center justify-between px-4 py-3 ${isChatActive ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}
                      aria-label={`Open chat${chatContext.totalUnreadCount > 0 ? ` (${chatContext.totalUnreadCount} unread)` : ''}`}
                    >
                      <span className="flex items-center space-x-3">
                        <MessageSquare size={20} />
                        <span>{t('chat')}</span>
                      </span>
                      {chatContext.totalUnreadCount > 0 && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                          {chatContext.totalUnreadCount > 99 ? '99+' : chatContext.totalUnreadCount}
                        </span>
                      )}
                    </button>
                  </li>
                )}
                <li>
                  <Link href="/scene">
                    <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/scene") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                      <MapPin size={20} />
                      <span>{t('scene')}</span>
                    </div>
                  </Link>
                </li>
                {canAccessZap && (
                  <li>
                    <Link href="/zap">
                      <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/zap") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                        <Zap size={20} />
                        <span>{t('zap')}</span>
                      </div>
                    </Link>
                  </li>
                )}
                {canAccessCrate && (
                  <li>
                    <Link href="/crate">
                      <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/crate") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                        <Package size={20} />
                        <span>{t('myCrate')}</span>
                      </div>
                    </Link>
                  </li>
                )}
                <li>
                  <Link href="/radio">
                    <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/radio") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                      <Radio size={20} />
                      <span>{t('radio')}</span>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/daily">
                    <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/daily") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                      <CalendarClock size={20} />
                      <span>{t('daily')}<sup className="ml-0.5 text-[8px] font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1 py-px rounded-full">beta</sup></span>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/settings">
                    <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/settings") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                      <div className="relative">
                        <Settings size={20} />
                      </div>
                      <span>{t('settings')}</span>
                    </div>
                  </Link>
                </li>
                {user && user.role === 'admin' && (
                  <li>
                    <Link href="/admin">
                      <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/admin") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                        <Shield size={20} />
                        <span>{t('admin')}</span>
                      </div>
                    </Link>
                  </li>
                )}
                <li>
                  <Link href="/support">
                    <div className={`nav-menu-item flex items-center space-x-3 px-4 py-3 ${isActive("/support") ? "active" : "text-gray-700 dark:text-foreground hover:text-gray-900 dark:hover:text-secondary/80"} rounded-lg font-medium cursor-pointer`}>
                      <HelpCircle size={20} />
                      <span>{t('support')}</span>
                    </div>
                  </Link>
                </li>

                {isAuthenticated && (
                  <li className="pt-4 border-t border-border">
                    <button
                      onClick={handleSignOut}
                      className="nav-menu-item w-full flex items-center space-x-3 px-4 py-3 text-red-500 dark:text-red-400 rounded-lg font-medium cursor-pointer"
                    >
                      <LogOut size={20} />
                      <span>{t('signOut')}</span>
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>
          )}

          {/* Page Content */}
          <div className="flex-1 px-2 sm:px-4 min-w-0">
            {children}
            {Capacitor.isNativePlatform() && <OtaVersionBadge />}
          </div>
        </div>
      </main>

      {/* Join League Dialog */}
      <JoinLeagueDialog 
        open={joinDialogOpen}
        onOpenChange={setJoinDialogOpen}
      />

      <ErrorDisplay />
    </div>
  );
}
